'use client';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';
import { useFormStatus } from 'react-dom';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import { BUTTON_TYPE } from '@/components/@base/button/type';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';

import {
  DATA_CY_CONTACT_US_INP_MESSAGE,
  DATA_CY_CONTACT_US_INP_NAME,
  DATA_CY_CONTACT_US_INP_RELEVANT_UNIT,
} from '@/constant/cypress';
import { pageLevelLocalization } from '@/constant/localization';

import { unitsData } from '../units';

export default function ContactUsForm() {
  const {
    content: { contact: contactLocalization },
  } = pageLevelLocalization;
  const { pending } = useFormStatus();
  const formRef = useRef<HTMLFormElement | null>(null);

  function submit(formData) {
    const unit = formData.get('unit');
    const message = formData.get('message');
    const full_name = formData.get('full_name');
    const text = [
      contactLocalization.form.name,
      ':',
      full_name,
      '\n',
      contactLocalization.form.relevant_unit,
      ':',
      unit,
      '\n',
      message,
    ];

    const Goftino = window.Goftino;

    Goftino.open();
    Goftino.sendMessage({ text: text.join('') });

    formRef?.current?.reset();
  }

  return (
    <>
      <Suspense>
        <OpenChatParam />
      </Suspense>
      <form action={submit} ref={formRef}>
        <Container className='flex flex-col gap-5'>
          <Text type={TEXT_TYPE.P} className='text-base font-medium tracking-tighter'>
            {contactLocalization.form.send_message}
          </Text>
          <Container className='mt-2 flex flex-col gap-5 md:flex-row'>
            <input
              data-cy={DATA_CY_CONTACT_US_INP_NAME}
              placeholder={contactLocalization.form.name}
              className='custom-input h-12 px-5'
              name='full_name'
            />
            <select data-cy={DATA_CY_CONTACT_US_INP_RELEVANT_UNIT} className='custom-input appearance-none px-5' name='unit'>
              <option value='' disabled selected>
                {contactLocalization.form.relevant_unit}
              </option>
              {unitsData.map((item, index) => (
                <option key={index} value={item.title}>
                  {item.title} - {item.subTitle}
                </option>
              ))}
            </select>
          </Container>
          <textarea
           data-cy={DATA_CY_CONTACT_US_INP_MESSAGE}
            name='message'
            rows={4}
            className='custom-input resize-none p-6'
            placeholder={contactLocalization.form.your_message}
          />
          <Button
            disabled={pending}
            type={BUTTON_TYPE.SUBMIT}
            className='min-w-40 self-end bg-[#81CF33] text-base font-medium tracking-tighter'
            size={SIZE_ENUM.MD}
          >
            {pending ? `${contactLocalization.form.send}...` : contactLocalization.form.send}
          </Button>
        </Container>
      </form>
    </>
  );
}

function OpenChatParam() {
  const searchParams = useSearchParams();
  const openChat = searchParams.get('openchat');

  useEffect(() => {
    function handleGoftinoReady() {
      window.Goftino.open();
    }

    if (openChat) {
      window.addEventListener('goftino_ready', handleGoftinoReady);
    }

    return () => {
      if (openChat) {
        window.removeEventListener('goftino_ready', handleGoftinoReady);
      }
    };
  }, [openChat]);

  return null;
}
